import { z } from 'zod';

import { VideoConfiguration } from './videoConfiguration';
import { EditingForm } from './editingForm';
import { ProjectManifest } from './projectManifest';
import { TemplateManifest } from './templateManifest';

/**
 * TODO: Replace usage of DeprecatedVideoDescriptor in the autofill service with more specific AutofillVideoDescriptorThin/AutofillVideoDescriptorHydrated types
 *
 * DeprecatedVideoDescriptor is a subset of a template bundle combined with a variant configuration. The
 * bundle data can be populated lazily as needed, using the slug.
 */
export interface DeprecatedVideoDescriptor {
  __templateSlug: string;

  // The variant configuration
  __activeConfiguration: VideoConfiguration;

  // Template bundle properties
  __cachedEditingForm?: EditingForm;

  __cachedEditingActions?: any;

  projectManifest?: ProjectManifest;

  templateManifest?: TemplateManifest;
}

const activeConfiguration = z.instanceof(Object).transform((obj) => obj as VideoConfiguration);
const projectManifest = z.instanceof(Object).transform((obj) => obj as ProjectManifest);
const templateManifest = z.instanceof(Object).transform((obj) => obj as TemplateManifest);
const cachedEditingForm = z.instanceof(Object).transform((obj) => obj as EditingForm);

export const AutofillVideoDescriptorThinSchema = z.object({
  __templateSlug: z.string(),
  __activeConfiguration: activeConfiguration,
});

/**
 * A thin video descriptor object which the autofill service may pass around which only includes
 * the template slug and the active configuration.
 *
 * Note that this will likely need to be expanded in the future to include more data as we start allowing
 * the autofill service to modify things outside of the active configuration.
 */
export type AutofillVideoDescriptorThin = z.infer<typeof AutofillVideoDescriptorThinSchema>;

export const AutofillVideoDescriptorHydratedSchema = AutofillVideoDescriptorThinSchema.extend({
  templateManifest,
  projectManifest,
  __cachedEditingForm: cachedEditingForm,
});

/**
 * A fully hydrated object with all available video descriptor data for use in the autofill service
 */
export type AutofillVideoDescriptorHydrated = z.infer<typeof AutofillVideoDescriptorHydratedSchema>;

export const VideoDescriptorSchema = z.object({
  __templateSlug: z.string(),
  // Use transforms to cast values to their proper types since we don't have these types defined with Zod
  __activeConfiguration: activeConfiguration,
  templateManifest: templateManifest,
  projectManifest: projectManifest,
});

export interface VideoDescriptor {
  // v0.9.0 is the only version that doesn't have a version field. We need to have the property
  // enumerated here to make it easier to deal with all versions in the migration engine, but
  // we can type it as `never` to indicate that it will never actually be present.
  version?: never;
  __templateSlug: string | null;
  // The variant configuration
  __activeConfiguration: VideoConfiguration;
  templateManifest: TemplateManifest;
  projectManifest: ProjectManifest;
}
